import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Phone,
  Mail,
  Calendar,
  Shield,
  FileText,
  Image,
  User,
  Key,
  Trash2,
  MessageCircle,
  Copy,
  Check,
  Upload,
  Eye,
  Download,
  RefreshCw,
  MapPin,
  Car,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import { userAPI } from '../../services/api';
import Button from '../../components/ui/Button';
import Card, { CardContent } from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CarLoader from '../../components/ui/CarLoader';
import Toast from '../../components/ui/Toast';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { formatName, formatDateTime } from '../../lib/utils';

const roleBadgeColors = {
  manager: 'bg-purple-100 text-purple-800 border-purple-200',
  accountant: 'bg-green-100 text-green-800 border-green-200',
  dispatcher: 'bg-blue-100 text-blue-800 border-blue-200',
  driver: 'bg-orange-100 text-orange-800 border-orange-200',
  staff: 'bg-gray-100 text-gray-800 border-gray-200'
};

const roleIcons = {
  manager: '👔',
  accountant: '📊',
  dispatcher: '📍',
  driver: '🚗',
  staff: '👤'
};

const documentTypes = [
  { value: 'license', label: 'Driving License' },
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other' }
];

// Permission labels mapping
const permissionLabels = {
  'journeys.view': { label: 'View Journeys', icon: '🚗', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  'journeys.edit': { label: 'Edit Journeys', icon: '✏️', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  'journeys.create': { label: 'Create Journeys', icon: '➕', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  'journeys.delete': { label: 'Delete Journeys', icon: '🗑️', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  'bookings.view': { label: 'View Bookings', icon: '📅', color: 'bg-green-50 text-green-700 border-green-200' },
  'bookings.edit': { label: 'Edit Bookings', icon: '✏️', color: 'bg-green-50 text-green-700 border-green-200' },
  'bookings.create': { label: 'Create Bookings', icon: '➕', color: 'bg-green-50 text-green-700 border-green-200' },
  'bookings.delete': { label: 'Delete Bookings', icon: '🗑️', color: 'bg-green-50 text-green-700 border-green-200' },
  'cars.view': { label: 'View Vehicles', icon: '🚙', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'cars.edit': { label: 'Edit Vehicles', icon: '✏️', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'cars.create': { label: 'Add Vehicles', icon: '➕', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'cars.delete': { label: 'Delete Vehicles', icon: '🗑️', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'staff.view': { label: 'View Staff', icon: '👥', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  'staff.edit': { label: 'Edit Staff', icon: '✏️', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  'staff.create': { label: 'Add Staff', icon: '➕', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  'staff.delete': { label: 'Delete Staff', icon: '🗑️', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  'reports.view': { label: 'View Reports', icon: '📊', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  'settings.view': { label: 'View Settings', icon: '⚙️', color: 'bg-gray-50 text-gray-700 border-gray-200' },
  'settings.edit': { label: 'Edit Settings', icon: '⚙️', color: 'bg-gray-50 text-gray-700 border-gray-200' },
};

const formatPermission = (perm) => {
  if (permissionLabels[perm]) {
    return permissionLabels[perm];
  }
  // Fallback for unknown permissions
  const parts = perm.split('.');
  const module = parts[0]?.charAt(0).toUpperCase() + parts[0]?.slice(1);
  const action = parts[1]?.charAt(0).toUpperCase() + parts[1]?.slice(1);
  return {
    label: `${action} ${module}`,
    icon: '🔐',
    color: 'bg-gray-50 text-gray-700 border-gray-200'
  };
};

const StaffDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Confirm', onConfirm: null });

  // Permissions edit state
  const [permModal, setPermModal] = useState(false);
  const [editPermissions, setEditPermissions] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);

  // Document upload state
  const [docModal, setDocModal] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('license');
  const [docExpiry, setDocExpiry] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchStaffDetails();
  }, [id]);

  const fetchStaffDetails = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getStaffById(id);
      setStaff(response.data.user);
    } catch (error) {
      console.error('Error fetching staff details:', error);
      setError('Failed to load staff details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setConfirm({
      isOpen: true,
      title: 'Deactivate Staff?',
      message: 'Are you sure you want to deactivate this staff member?',
      type: 'warning',
      confirmText: 'Deactivate',
      onConfirm: async () => {
        try {
          await userAPI.deleteStaff(id);
          setToast({
            isOpen: true,
            title: 'Staff Deactivated',
            message: 'The staff member has been deactivated successfully.',
            type: 'success'
          });
          setTimeout(() => navigate('/staff'), 1500);
        } catch (error) {
          console.error('Error deleting staff:', error);
          setToast({
            isOpen: true,
            title: 'Error',
            message: 'Failed to deactivate staff member. Please try again.',
            type: 'error'
          });
        }
      }
    });
  };

  const copyCredentials = () => {
    if (!staff) return;
    const text = `Login Credentials for ${staff.name}\n\nPhone (Login ID): ${staff.phone}\nLogin URL: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWhatsApp = () => {
    if (!staff) return;
    const text = `Login Credentials\n\nName: ${staff.name}\nRole: ${roleIcons[staff.role]} ${staff.role}\nPhone (Login ID): ${staff.phone}\nLogin URL: ${window.location.origin}/login`;
    const whatsappUrl = `https://wa.me/${staff.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setToast({
          isOpen: true,
          title: 'File Too Large',
          message: 'File size must be less than 10MB',
          type: 'error'
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDocUpload = async () => {
    if (!selectedFile) {
      setToast({
        isOpen: true,
        title: 'No File Selected',
        message: 'Please select a file to upload',
        type: 'error'
      });
      return;
    }

    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('documentType', selectedDocType);
      if (docExpiry) {
        formData.append('expiryDate', docExpiry);
      }

      await userAPI.uploadStaffDocument(id, formData);

      setToast({
        isOpen: true,
        title: 'Document Uploaded',
        message: 'Document has been uploaded successfully.',
        type: 'success'
      });

      setDocModal(false);
      setSelectedFile(null);
      setSelectedDocType('license');
      setDocExpiry('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      fetchStaffDetails();
    } catch (error) {
      console.error('Error uploading document:', error);
      setToast({
        isOpen: true,
        title: 'Upload Failed',
        message: 'Failed to upload document. Please try again.',
        type: 'error'
      });
    } finally {
      setDocUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    setConfirm({
      isOpen: true,
      title: 'Delete Document?',
      message: 'Are you sure you want to delete this document?',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await userAPI.deleteStaffDocument(id, docId);
          setToast({
            isOpen: true,
            title: 'Document Deleted',
            message: 'Document has been deleted successfully.',
            type: 'success'
          });
          fetchStaffDetails();
        } catch (error) {
          console.error('Error deleting document:', error);
          setToast({
            isOpen: true,
            title: 'Delete Failed',
            message: 'Failed to delete document. Please try again.',
            type: 'error'
          });
        }
      }
    });
  };

  // Permission groups for the editor
  const PERMISSION_GROUPS = {
    'Vehicles': ['cars.view', 'cars.create', 'cars.edit', 'cars.delete'],
    'Bookings': ['bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete', 'bookings.assign_driver'],
    'Drivers': ['drivers.view', 'drivers.create', 'drivers.edit', 'drivers.delete'],
    'Users': ['users.view', 'users.create', 'users.edit', 'users.delete'],
    'Payments': ['payments.view', 'payments.create', 'payments.edit'],
    'Reports': ['reports.view', 'reports.export'],
    'Journeys': ['journeys.view', 'journeys.create', 'journeys.edit'],
    'Maintenance': ['maintenance.view', 'maintenance.create', 'maintenance.edit'],
    'Settings': ['settings.view', 'settings.edit']
  };

  const openPermModal = () => {
    setEditPermissions([...(staff.permissions || [])]);
    setPermModal(true);
  };

  const togglePermission = (perm) => {
    setEditPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const toggleGroup = (perms) => {
    const allSelected = perms.every(p => editPermissions.includes(p));
    if (allSelected) {
      setEditPermissions(prev => prev.filter(p => !perms.includes(p)));
    } else {
      setEditPermissions(prev => [...new Set([...prev, ...perms])]);
    }
  };

  const handleSavePermissions = async () => {
    setSavingPerms(true);
    try {
      await userAPI.updateStaffPermissions(id, editPermissions);
      setStaff(prev => ({ ...prev, permissions: editPermissions }));
      setPermModal(false);
      setToast({
        isOpen: true,
        title: 'Permissions Updated',
        message: 'Staff permissions have been updated successfully.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating permissions:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update permissions. Please try again.',
        type: 'error'
      });
    } finally {
      setSavingPerms(false);
    }
  };

  const getDocTypeLabel = (type) => {
    const found = documentTypes.find(d => d.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <div className="p-6">
        <CarLoader message="Loading staff details..." />
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Staff Member Not Found</h3>
            <p className="text-gray-600 mb-4">{error || 'The requested staff member could not be found.'}</p>
            <Button onClick={() => navigate('/staff')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Team List
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff Details"
        description={`View and manage ${formatName(staff?.name) || 'staff member'}`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/staff')}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <Button variant="primary" onClick={() => navigate(`/staff/${id}/edit`)}>
              <Edit className="h-5 w-5 mr-2" />
              Edit
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Card */}
        <div className="lg:col-span-1">
          <Card className="overflow-hidden">
            {/* Photo - Square, full width */}
            <div className={`w-full aspect-square flex items-center justify-center overflow-hidden ${
              staff.isActive ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gray-400'
            }`}>
              {staff.photo?.url ? (
                <img src={staff.photo.url} alt={staff.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-7xl font-bold">
                  {formatName(staff.name)?.charAt(0) || 'U'}
                </span>
              )}
            </div>

            <CardContent className="p-5">
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">{formatName(staff.name)}</h2>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${roleBadgeColors[staff.role]}`}>
                    {roleIcons[staff.role]} {staff.role.charAt(0).toUpperCase() + staff.role.slice(1)}
                  </span>
                  {staff.isActive ? (
                    <span className="px-2.5 py-1 rounded-full text-xs bg-green-100 text-green-800 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Active
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full text-xs bg-red-100 text-red-800 flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-5">
                <Button variant="outline" className="flex-1" onClick={() => navigate(`/staff/${id}/edit`)}>
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setDocModal(true)}>
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Login Credentials Card */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <Key className="h-4 w-4 mr-2 text-blue-600" /> Login Credentials
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Login ID (Phone)</p>
                  <p className="text-lg font-mono font-bold text-gray-900">{staff.phone}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Login URL</p>
                  <p className="text-sm font-mono text-blue-600 break-all">{window.location.origin}/login</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1" onClick={copyCredentials}>
                  {copied ? <><Check className="h-4 w-4 mr-1 text-green-600" /> Copied</> : <><Copy className="h-4 w-4 mr-1" /> Copy</>}
                </Button>
                {staff.phone && (
                  <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={shareViaWhatsApp}>
                    <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <Phone className="h-4 w-4 mr-2 text-blue-600" /> Contact Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Phone className="h-4 w-4 text-blue-600 mr-2" />
                    <p className="text-xs text-gray-500">Phone</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{staff.phone || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Mail className="h-4 w-4 text-gray-500 mr-2" />
                    <p className="text-xs text-gray-500">Email</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">{staff.email || '-'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                    <p className="text-xs text-gray-500">Date of Birth</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {staff.dateOfBirth ? new Date(staff.dateOfBirth).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Clock className="h-4 w-4 text-gray-500 mr-2" />
                    <p className="text-xs text-gray-500">Joined</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {staff.createdAt ? new Date(staff.createdAt).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>

              {staff.address && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <MapPin className="h-4 w-4 text-gray-500 mr-2" />
                    <p className="text-xs text-gray-500">Address</p>
                  </div>
                  <p className="text-sm text-gray-900">{staff.address}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Section */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-blue-600" /> Documents
                </h3>
                <Button size="sm" onClick={() => setDocModal(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Upload
                </Button>
              </div>

              {staff.documents && staff.documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {staff.documents.map((doc) => (
                    <div
                      key={doc._id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center min-w-0">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{getDocTypeLabel(doc.type)}</p>
                          {doc.expiryDate && (
                            <p className="text-xs text-gray-500">
                              Expires: {new Date(doc.expiryDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Document"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteDoc(doc._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Document"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-3">No documents uploaded yet</p>
                  <Button size="sm" variant="outline" onClick={() => setDocModal(true)}>
                    <Upload className="h-4 w-4 mr-2" /> Upload Document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-blue-600" /> Permissions
                </h3>
                <Button variant="outline" size="sm" onClick={openPermModal}>
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </div>
              {staff.permissions && staff.permissions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {staff.permissions.map((perm) => {
                    const permInfo = formatPermission(perm);
                    return (
                      <span key={perm} className={`px-3 py-1.5 border rounded-lg text-sm font-medium flex items-center gap-1.5 ${permInfo.color}`}>
                        <span>{permInfo.icon}</span>
                        {permInfo.label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No specific permissions assigned</p>
              )}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" /> Danger Zone
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Deactivate this user to prevent them from logging in</p>
                </div>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Deactivate
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Document Upload Modal */}
      <Modal
        isOpen={docModal}
        onClose={() => {
          setDocModal(false);
          setSelectedFile(null);
          setSelectedDocType('license');
          setDocExpiry('');
        }}
        title="Upload Document"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Document Type"
            value={selectedDocType}
            onChange={(e) => setSelectedDocType(e.target.value)}
            options={documentTypes}
          />

          <Input
            label="Expiry Date (Optional)"
            type="date"
            value={docExpiry}
            onChange={(e) => setDocExpiry(e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center">
                  <FileText className="h-8 w-8 text-blue-600 mr-3" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Click to select a file</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, PDF (Max 10MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setDocModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleDocUpload} disabled={docUploading || !selectedFile}>
              {docUploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" /> Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Permissions Edit Modal */}
      {permModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Edit Permissions</h3>
                <p className="text-blue-100 text-sm">{formatName(staff.name)} - {staff.role}</p>
              </div>
              <button
                onClick={() => setPermModal(false)}
                className="text-white/70 hover:text-white p-1"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(PERMISSION_GROUPS).map(([group, perms]) => {
                  const selectedCount = perms.filter(p => editPermissions.includes(p)).length;
                  const allSelected = selectedCount === perms.length;
                  return (
                    <div key={group} className="border rounded-lg p-3">
                      <label className="flex items-center justify-between mb-2 cursor-pointer">
                        <span className="text-sm font-semibold text-gray-800">{group}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{selectedCount}/{perms.length}</span>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleGroup(perms)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </div>
                      </label>
                      <div className="space-y-1.5">
                        {perms.map(perm => {
                          const action = perm.split('.')[1]?.replace('_', ' ');
                          return (
                            <label key={perm} className="flex items-center gap-2 cursor-pointer py-0.5">
                              <input
                                type="checkbox"
                                checked={editPermissions.includes(perm)}
                                onChange={() => togglePermission(perm)}
                                className="w-3.5 h-3.5 text-blue-600 rounded"
                              />
                              <span className="text-xs text-gray-600 capitalize">{action}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">{editPermissions.length} permissions selected</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setPermModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePermissions}
                  disabled={savingPerms}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {savingPerms ? 'Saving...' : 'Save Permissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirm.isOpen}
        onClose={() => setConfirm(p => ({ ...p, isOpen: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        confirmText={confirm.confirmText}
      />

      <Toast
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default StaffDetails;
